import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Banner } from '@eshop/types/banners.types.ts';
import type { TemplateGridSpan } from '@eshop/templates';
import { buildCatalogueSizes, buildCatalogueSrcSet } from '@eshop/templates/visual/shared';
import { getImageUrl, IMG_NOT_FOUND } from '@tools/utils/imageUtils.tsx';
import { logError, logInfo } from '@tools/LogsHandler.tsx';

import CatalogBannerRow from './CatalogBannerRow';

// Tipos de animaciones predefinidas
export interface BannerAnimation {
  name: string;
  label: string;
  /** Estado inicial + duración/ease para el contenedor */
  containerAnimation: gsap.TweenVars;
  /** Estado inicial + duración/ease para los textos */
  textAnimation: gsap.TweenVars;
  /** Estado inicial + duración/ease para la imagen */
  imageAnimation?: gsap.TweenVars;
  /** Retraso inicial para animar el contenedor */
  containerDelay?: number;
  /** Retraso para empezar a animar los textos luego del contenedor */
  textDelay?: number;
  /** Retraso para empezar a animar la imagen */
  imageDelay?: number;
  /**
   * Compat: si tenías `delay` antes, lo tomamos como `containerDelay`.
   * (Preferí usar `containerDelay` y `textDelay` de ahora en más)
   */
  delay?: number;
}

// Catálogo de animaciones disponibles
export const BANNER_ANIMATIONS: Record<string, BannerAnimation> = {
  slideInLeft: {
    name: 'slideInLeft',
    label: 'Deslizar desde la izquierda',
    containerAnimation: {
      x: -100,
      opacity: 0,
      duration: 0.8,
      ease: 'power2.out'
    },
    textAnimation: {
      y: 20,
      opacity: 0,
      duration: 0.6,
      ease: 'power2.out',
      stagger: 0.1
    },
    imageAnimation: {
      scale: 0.9,
      opacity: 0,
      duration: 0.7,
      ease: 'power2.out'
    },
    containerDelay: 0.2,
    textDelay: 0.15,
    imageDelay: 0.1,
  },
  slideInRight: {
    name: 'slideInRight',
    label: 'Deslizar desde la derecha',
    containerAnimation: {
      x: 100,
      opacity: 0,
      duration: 0.8,
      ease: 'power2.out'
    },
    textAnimation: {
      y: 20,
      opacity: 0,
      duration: 0.6,
      ease: 'power2.out',
      stagger: 0.1
    },
    imageAnimation: {
      scale: 0.9,
      opacity: 0,
      duration: 0.7,
      ease: 'power2.out'
    },
    containerDelay: 0.2,
    textDelay: 0.15,
    imageDelay: 0.1,
  },
  fadeInUp: {
    name: 'fadeInUp',
    label: 'Desvanecer hacia arriba',
    containerAnimation: {
      y: 50,
      opacity: 0,
      duration: 1,
      ease: 'power3.out'
    },
    textAnimation: {
      y: 30,
      opacity: 0,
      duration: 0.8,
      ease: 'power3.out',
      stagger: 0.15
    },
    imageAnimation: {
      y: 40,
      scale: 0.95,
      opacity: 0,
      duration: 0.9,
      ease: 'power3.out'
    },
    containerDelay: 0.3,
    textDelay: 0.2,
    imageDelay: 0.1,
  },
  zoomIn: {
    name: 'zoomIn',
    label: 'Zoom de entrada',
    containerAnimation: {
      scale: 0.8,
      opacity: 0,
      duration: 0.7,
      ease: 'back.out(1.7)'
    },
    textAnimation: {
      scale: 0.9,
      opacity: 0,
      duration: 0.6,
      ease: 'back.out(1.7)',
      stagger: 0.1
    },
    imageAnimation: {
      scale: 0.7,
      opacity: 0,
      duration: 0.8,
      ease: 'back.out(1.7)'
    },
    containerDelay: 0.1,
    textDelay: 0.15,
    imageDelay: 0.05,
  },
  slideInDown: {
    name: 'slideInDown',
    label: 'Deslizar desde arriba',
    containerAnimation: {
      y: -80,
      opacity: 0,
      duration: 0.9,
      ease: 'power2.out'
    },
    textAnimation: {
      y: -20,
      opacity: 0,
      duration: 0.7,
      ease: 'power2.out',
      stagger: 0.1
    },
    imageAnimation: {
      y: -60,
      scale: 0.9,
      opacity: 0,
      duration: 0.8,
      ease: 'power2.out'
    },
    containerDelay: 0.2,
    textDelay: 0.15,
    imageDelay: 0.05,
  },
  bounceIn: {
    name: 'bounceIn',
    label: 'Entrada rebotante',
    containerAnimation: {
      scale: 0.3,
      opacity: 0,
      duration: 0.8,
      ease: 'bounce.out'
    },
    textAnimation: {
      y: 25,
      opacity: 0,
      duration: 0.6,
      ease: 'bounce.out',
      stagger: 0.12
    },
    imageAnimation: {
      scale: 0.4,
      opacity: 0,
      duration: 0.9,
      ease: 'bounce.out'
    },
    containerDelay: 0.1,
    textDelay: 0.12,
    imageDelay: 0.05,
  },
};

// simple console based telemetry
export const track = (type: string, id: number) => {
  // eslint-disable-next-line no-console
  logInfo(`banner:${type}`, id);
};

interface Props {
  banner: Banner;
  className?: string;
  breakpoints?: TemplateGridSpan;
  imageSizes?: string;
  imageLoading?: 'lazy' | 'eager';
  renderAsChild?: boolean;
  visible?: boolean;
  aspectRatio?: string;
}

const CatalogBanner: React.FC<Props> = ({
  banner,
  className,
  breakpoints,
  imageSizes,
  imageLoading,
  renderAsChild = false,
  visible = true,
  aspectRatio,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const textContainerRef = useRef<HTMLDivElement | null>(null);
  const mediaWrapperRef = useRef<HTMLDivElement | null>(null);
  const headlineRef = useRef<HTMLHeadingElement | null>(null);
  const bodyRef = useRef<HTMLParagraphElement | null>(null);
  const ctaRef = useRef<HTMLSpanElement | null>(null);
  const [isAnimated, setIsAnimated] = useState(false);

  const { srcDefault, srcSet: derivedSrcSet } = getImageUrl(banner.image);

  const animationConfig = useMemo<BannerAnimation>(() => {
    // Si viene como string: usar predefinida o fallback
    if (typeof banner.animation === "string") {
      return BANNER_ANIMATIONS[banner.animation] || BANNER_ANIMATIONS.slideInRight;
    }

    // Si viene como objeto:
    if (banner.animation && typeof banner.animation === "object") {
      const name = banner.animation.name || "slideInRight";
      const base = BANNER_ANIMATIONS[name] || BANNER_ANIMATIONS["slideInRight"];

      // Merge con overrides opcionales
      return {
        ...base,
        ...(banner.animation.containerAnimation
          ? { containerAnimation: banner.animation.containerAnimation }
          : {}),
        ...(banner.animation.textAnimation
          ? { textAnimation: banner.animation.textAnimation }
          : {}),
        ...(banner.animation.imageAnimation
          ? { imageAnimation: banner.animation.imageAnimation }
          : {}),
        ...(typeof banner.animation.containerDelay === "number"
          ? { containerDelay: banner.animation.containerDelay }
          : {}),
        ...(typeof banner.animation.textDelay === "number"
          ? { textDelay: banner.animation.textDelay }
          : {}),
        ...(typeof banner.animation.imageDelay === "number"
          ? { imageDelay: banner.animation.imageDelay }
          : {}),
      };
    }

    // Fallback
    return BANNER_ANIMATIONS.slideInRight;
  }, [banner.animation]);

  useEffect(() => {
    if (!visible) return;

    let ctx: gsap.Context | null = null;
    let tl: gsap.core.Timeline | null = null;
    let killed = false;

    (async () => {
      const { default: gsap } = await import("gsap");

      if (killed) return;
      const container = containerRef.current;
      const mediaElement = mediaWrapperRef.current;
      if (!container) return;

      // Check reduced motion preference
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) {
        // Instant visual state without animation
        gsap.set(container, {
          x: 0,
          y: 0,
          scale: 1,
          opacity: 1,
          clearProps: "transform"
        });
        if (mediaElement) {
          gsap.set(mediaElement, { opacity: 1, scale: 1 });
        }
        setIsAnimated(true);
        return;
      }

      const textElements = [
        headlineRef.current,
        bodyRef.current,
        ctaRef.current,
      ].filter(Boolean) as HTMLElement[];

      // Defaults para evitar NaN si no vienen durations
      const containerDuration = (animationConfig.containerAnimation?.duration as number) ?? 0.8;
      const textDuration = (animationConfig.textAnimation?.duration as number) ?? 0.6;
      const imageDuration = (animationConfig.imageAnimation?.duration as number) ?? 0.7;

      const containerDelay = animationConfig.containerDelay ?? animationConfig.delay ?? 0;
      const textDelay = animationConfig.textDelay ?? 0.15;
      const imageDelay = animationConfig.imageDelay ?? 0.1;

      ctx = gsap.context(() => {
        // Estado inicial del contenedor
        gsap.set(container, {
          ...animationConfig.containerAnimation,
          clearProps: "none",
        });

        // Estado inicial de la imagen
        if (mediaElement && animationConfig.imageAnimation) {
          gsap.set(mediaElement, {
            ...animationConfig.imageAnimation,
            clearProps: "none",
          });
        }

        // Estado inicial de los textos
        if (textElements.length) {
          gsap.set(textElements, {
            ...animationConfig.textAnimation,
            clearProps: "none",
          });
        }

        tl = gsap.timeline();

        // 1) Animar contenedor primero
        tl.to(
          container,
          {
            // mismos tiempos/ease que el preset, pero moviendo hacia estado "visible"
            duration: containerDuration,
            ease: (animationConfig.containerAnimation?.ease as gsap.EaseString) || "power2.out",
            x: 0,
            y: 0,
            scale: 1,
            opacity: 1,
          },
          containerDelay
        );

        // 2) Animar imagen con su propio delay
        if (mediaElement && animationConfig.imageAnimation) {
          tl.to(
            mediaElement,
            {
              duration: imageDuration,
              ease: (animationConfig.imageAnimation?.ease as gsap.EaseString) || "power2.out",
              x: 0,
              y: 0,
              scale: 1,
              opacity: 1,
            },
            containerDelay + imageDelay
          );
        }

        // 3) Animar textos con su propio delay
        if (textElements.length) {
          tl.to(
            textElements,
            {
              duration: textDuration,
              ease: (animationConfig.textAnimation?.ease as gsap.EaseString) || "power2.out",
              x: 0,
              y: 0,
              scale: 1,
              opacity: 1,
              stagger: (animationConfig.textAnimation?.stagger as number) ?? 0.1,
            },
            containerDelay + textDelay
          );
        }
      }, container);

      setIsAnimated(true);
    })();

    return () => {
      killed = true;
      if (tl) {
        tl.kill();
        tl = null;
      }
      if (ctx) {
        ctx.revert();
        ctx = null;
      }
    };
  }, [animationConfig, banner.id, visible]);

  /** Pasa a imagen de reserva y vacía el srcSet para evitar nuevos 404. */
  const handleImageError = () => {
    logError('Error loading image:', banner.image);
  };

  const handleBannerMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    event.currentTarget.style.transform = 'translateY(-4px)';
    event.currentTarget.style.boxShadow = 'var(--catalogue-card-shadow-strong, 0 16px 48px rgba(0, 0, 0, 0.18))';
  };

  const handleBannerMouseLeave = (event: React.MouseEvent<HTMLDivElement>) => {
    event.currentTarget.style.transform = 'translateY(0)';
    event.currentTarget.style.boxShadow = 'var(--catalogue-card-shadow)';
  };

  const handleMediaMouseEnter = (event: React.MouseEvent<HTMLImageElement>) => {
    if (isAnimated) {
      event.currentTarget.style.transform = 'scale(1.05)';
    }
  };

  const handleMediaMouseLeave = (event: React.MouseEvent<HTMLImageElement>) => {
    if (isAnimated) {
      event.currentTarget.style.transform = 'scale(1)';
    }
  };

  if (!visible) {
    return null;
  }

  const resolvedSrcSet = buildCatalogueSrcSet({ derived: derivedSrcSet });
  const computedSizes = buildCatalogueSizes({ variant: 'banner', fallback: imageSizes });
  const resolvedSizes = resolvedSrcSet ? computedSizes : imageSizes ?? undefined;
  const isTopPlacement = banner.locations?.some((location) => location?.includes('top')) ?? false;
  const resolvedLoading = imageLoading ?? (isTopPlacement ? 'eager' : 'lazy');
  const resolvedFetchPriority = resolvedLoading === 'eager' ? 'high' : undefined;
  const resolvedDecoding = resolvedLoading === 'eager' ? 'sync' : 'async';

  return (
    <CatalogBannerRow
      banner={banner}
      className={className}
      breakpoints={breakpoints}
      renderAsChild={renderAsChild}
      containerRef={containerRef}
      mediaWrapperRef={mediaWrapperRef}
      textContainerRef={textContainerRef}
      headlineRef={headlineRef}
      bodyRef={bodyRef}
      ctaRef={ctaRef}
      media={{
        src: srcDefault,
        srcSet: resolvedSrcSet,
        sizes: resolvedSizes,
        fallback: IMG_NOT_FOUND,
        loading: resolvedLoading,
        decoding: resolvedDecoding,
        fetchPriority: resolvedFetchPriority,
        aspectRatio: aspectRatio,
        onError: handleImageError,
      }}
      onBannerMouseEnter={handleBannerMouseEnter}
      onBannerMouseLeave={handleBannerMouseLeave}
      onMediaMouseEnter={handleMediaMouseEnter}
      onMediaMouseLeave={handleMediaMouseLeave}
      onLinkClick={() => track('click', banner.id)}
    />
  );
};

export default CatalogBanner;
